import type { IHttpService, ILogService, IQService } from "angular";
import { module } from "angular";

import type { Observer, TeardownLogic } from "rxjs";
import { Observable } from "rxjs";

import type { IConfig } from "App/Modules/Utils";

import type { IHttpService as IMmHttpService } from "App/Modules/Services";
import type { IResponse } from "../../Models";

export class HttpService implements IMmHttpService {
    public static $inject = ["$http", "$q", "$log", "irConfig"];

    constructor(
        private $http: IHttpService,
        private $q: IQService,
        private $log: ILogService,
        private config: IConfig,
    ) {}

    public request<T>(method: string, path: string, data: any = null, params: any = null): Observable<IResponse<T>> {
        return new Observable<IResponse<T>>((observer: Observer<IResponse<T>>) => {
            let requestFinished = false;
            const canceler = this.$q.defer();
            // this.$log.debug("Sending request");
            const conf = this.config;
            this.$http<T>({
                data,
                method,
                params,
                timeout: canceler.promise,
                url: conf.apiUrl + path,
            })
                .then((r) => {
                    requestFinished = true;
                    // tslint:disable:object-literal-sort-keys
                    const result: IResponse<T> = {
                        data: undefined,
                        hasError: false,
                        status: r.status,
                        statusText: r.statusText,
                        isSuccessful: true,
                    };
                    if (r.data) {
                        result.data = r.data;
                    }
                    Object.assign(result, r);
                    observer.next(result);
                    observer.complete();
                })
                .catch((e: angular.IHttpResponse<any>) => {
                    requestFinished = true;
                    // tslint:disable:object-literal-sort-keys
                    const error: IResponse<any> = {
                        hasError: true,
                        error: undefined,
                        status: e.status,
                        statusText: e.statusText,
                        isSuccessful: false,
                        headers: e.headers,
                    };
                    if (e.data) {
                        error.error = e.data;
                    }
                    Object.assign(error, e);
                    if (error.data) {
                        delete error.data;
                    }
                    observer.error(error);
                });

            const teardownLogic: TeardownLogic = () => {
                if (!requestFinished) {
                    this.$log.debug(
                        `Http request cancelled [${method}] ${this.config.apiUrl + path} (often by switchMap)`,
                    );
                }
                canceler.resolve();
            };
            return teardownLogic;
        });
    }

    public get<T>(path: string, params: any = null): Observable<IResponse<T>> {
        return this.request<T>("GET", path, null, params);
    }

    public post<T>(path: string, data: any): Observable<IResponse<T>> {
        return this.request<T>("POST", path, data);
    }

    public put<T>(path: string, data: any): Observable<IResponse<T>> {
        return this.request<T>("PUT", path, data);
    }

    public patch<T>(path: string, data: any): Observable<IResponse<T>> {
        return this.request<T>("PATCH", path, data);
    }

    public delete(path: string): Observable<IResponse<void>> {
        return this.request<void>("DELETE", path);
    }
}

module("mmServices").service("mmHttpService", HttpService);
